import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { enforceModuleDependencies, getAccountingCoreDependencies, canToggleModule, getModuleDisabledReason } from '../utils/moduleGating';
import { isAccountingClient } from '../utils/tenantType';

const TenantSettingsModal = ({ isOpen, tenant, onClose, onSave, mode = 'edit' }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settingsData, setSettingsData] = useState({
    name: '',
    username: '',
    password: '',
    subscriptionPlan: 'starter',
    isActive: true,
    tenantType: 'retail',
    accountingMode: 'gl_only',
    complianceProfile: 'none',
    createDefaultStore: true,
    defaultStoreName: '',
    assignAdminToDefaultStore: true,
    enabledModules: {
      accountingCore: true,
      accountingReports: true,
      birBooks: false,
      tax: false,
      vat: false,
      ar: false,
      ap: false,
      invoicing: false,
      billing: false,
      pos: false,
      inventory: false,
      attendance: false
    }
  });

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
  const { getAccessToken } = useAuth();

  // Fetch tenant settings when modal opens (edit mode only)
  useEffect(() => {
    if (isOpen && mode === 'edit' && tenant) {
      fetchSettings();
    } else if (isOpen && mode === 'create') {
      // Reset to defaults for create mode
      setSettingsData({
        name: '',
        username: '',
        password: '',
        subscriptionPlan: 'starter',
        isActive: true,
        tenantType: 'retail',
        accountingMode: 'gl_only',
        complianceProfile: 'none',
        createDefaultStore: true,
        defaultStoreName: '',
        assignAdminToDefaultStore: true,
        enabledModules: {
          accountingCore: true,
          accountingReports: true,
          birBooks: false,
          tax: false,
          vat: false,
          ar: false,
          ap: false,
          invoicing: false,
          billing: false,
          pos: false,
          inventory: false,
          attendance: false
        }
      });
    }
  }, [isOpen, tenant, mode]);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAccessToken();
      const res = await api.get(`/tenant/${tenant._id}/settings`);
      setSettingsData({
        tenantType: res.data.tenantType || 'retail',
        accountingMode: res.data.accountingMode || 'gl_only',
        complianceProfile: res.data.complianceProfile || 'none',
        createDefaultStore: res.data.createDefaultStore !== undefined ? res.data.createDefaultStore : true,
        defaultStoreName: res.data.defaultStoreName || tenant.name || '',
        assignAdminToDefaultStore: res.data.assignAdminToDefaultStore !== undefined ? res.data.assignAdminToDefaultStore : true,
        enabledModules: res.data.enabledModules || settingsData.enabledModules
      });
    } catch (err) {
      console.error('Error fetching tenant settings:', err);
      setError('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantTypeChange = (e) => {
    setSettingsData({ ...settingsData, tenantType: e.target.value });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsData({
      ...settingsData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const generatePassword = () => {
    const randomPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-2).toUpperCase();
    setSettingsData({ ...settingsData, password: randomPassword });
  };

  const handleAccountingModeChange = (e) => {
    setSettingsData({ ...settingsData, accountingMode: e.target.value });
  };

  const handleComplianceProfileChange = (e) => {
    setSettingsData({ ...settingsData, complianceProfile: e.target.value });
  };

  const handleCreateDefaultStoreChange = (e) => {
    setSettingsData({ ...settingsData, createDefaultStore: e.target.checked });
  };

  const handleDefaultStoreNameChange = (e) => {
    const fallbackName = mode === 'edit' ? tenant?.name : settingsData.name;
    setSettingsData({ ...settingsData, defaultStoreName: e.target.value || fallbackName });
  };

  const handleAssignAdminToDefaultStoreChange = (e) => {
    setSettingsData({ ...settingsData, assignAdminToDefaultStore: e.target.checked });
  };

  const handleModuleToggle = (moduleKey) => {
    const newValue = !settingsData.enabledModules[moduleKey];
    const updated = enforceModuleDependencies(settingsData.enabledModules, moduleKey, newValue);
    setSettingsData({
      ...settingsData,
      enabledModules: updated
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    // Validation for create mode
    if (mode === 'create') {
      if (!settingsData.name || !settingsData.username || !settingsData.password) {
        setError('Name, Username, and Password are required');
        setSaving(false);
        return;
      }
    }
    
    try {
      const token = getAccessToken();
      let res;
      
      if (mode === 'create') {
        // Create new tenant
        res = await api.post('/tenant', settingsData);
        setSuccess(`Tenant created successfully!\nUsername: ${res.data.adminUser.username}\nPassword: ${res.data.adminUser.password}`);
        onSave(res.data.tenant);
        setTimeout(() => {
          onClose();
        }, 3000); // Longer delay to read credentials
      } else {
        // Update existing tenant settings
        res = await api.put(`/tenant/${tenant._id}/settings`, settingsData);
        setSuccess('Settings saved successfully');
        onSave(res.data);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving tenant:', err);
      setError(err.response?.data?.message || `Failed to ${mode === 'create' ? 'create tenant' : 'save settings'}`);
    } finally {
      setSaving(false);
    }
  };

  // Show accounting settings if tenantType is accounting_firm OR accountingCore is enabled
  const showAccountingSettings = 
    isAccountingClient(settingsData.tenantType) || 
    settingsData.enabledModules.accountingCore;

  // Helper to render module toggle with disabled state
  const renderModuleToggle = (moduleKey, label) => {
    const isDisabled = !canToggleModule(moduleKey, settingsData.enabledModules);
    const disabledReason = getModuleDisabledReason(moduleKey, settingsData.enabledModules);
    const isChecked = settingsData.enabledModules[moduleKey];

    return (
      <div key={moduleKey} className="relative">
        <label 
          className={`flex items-center ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={disabledReason || ''}
        >
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => !isDisabled && handleModuleToggle(moduleKey)}
            disabled={isDisabled}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
          <span className={`ml-2 ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
            {label}
          </span>
        </label>
        {disabledReason && (
          <p className="text-xs text-red-500 ml-6 mt-1">
            ⚠️ {disabledReason}
          </p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-300 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {mode === 'create' ? 'Create New Tenant' : `Tenant Settings: ${tenant?.name}`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {loading ? (
            <p className="text-center py-8">Loading settings...</p>
          ) : (
            <>
              {/* Section 0: Basic Info (Create Mode Only) */}
              {mode === 'create' && (
                <div className="mb-8 bg-blue-50 p-4 rounded border border-blue-200">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Basic Information</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tenant Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={settingsData.name}
                      onChange={handleChange}
                      placeholder="e.g., Fresh Foods Inc."
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={settingsData.username}
                      onChange={handleChange}
                      placeholder="e.g., admin_freshfoods"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Password <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        name="password"
                        value={settingsData.password}
                        onChange={handleChange}
                        placeholder="Enter strong password"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={generatePassword}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Generate
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Plan
                    </label>
                    <select
                      name="subscriptionPlan"
                      value={settingsData.subscriptionPlan}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="starter">Starter</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={settingsData.isActive}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Section 1: Tenant Type */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Tenant Type</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Tenant Type
                  </label>
                  <select
                    value={settingsData.tenantType}
                    onChange={handleTenantTypeChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="accounting_firm">Accounting Firm</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="retail">Retail</option>
                    <option value="mrwd">MRWD</option>
                      <option value="accounting_operations">Accounting Operations</option>
                  </select>
                </div>
              </div>

              {/* Section 1B: Store Defaults */}
              <div className="mb-8 bg-green-50 p-4 rounded border border-green-200">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Store Setup (Optional)</h3>
                
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settingsData.createDefaultStore}
                      onChange={handleCreateDefaultStoreChange}
                      className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="ml-2 text-gray-700 font-medium">Create Default Store</span>
                  </label>
                  <p className="text-xs text-gray-600 ml-6 mt-1">
                    Automatically create a main store for this tenant on first setup
                  </p>
                </div>

                {settingsData.createDefaultStore && (
                  <>
                    <div className="mb-4 ml-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Store Name
                      </label>
                      <input
                        type="text"
                        value={settingsData.defaultStoreName}
                        onChange={handleDefaultStoreNameChange}
                        placeholder={tenant?.name || 'Store Name'}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Defaults to tenant name if left empty
                      </p>
                    </div>

                    <div className="mb-4 ml-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settingsData.assignAdminToDefaultStore}
                          onChange={handleAssignAdminToDefaultStoreChange}
                          className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                        />
                        <span className="ml-2 text-gray-700">Auto-assign Admin to Default Store</span>
                      </label>
                      <p className="text-xs text-gray-600 ml-6 mt-1">
                        Grant the admin user access to the default store automatically
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Section 2: Accounting Settings (conditional) */}
              {showAccountingSettings && (
                <div className="mb-8 bg-blue-50 p-4 rounded border border-blue-200">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Accounting Settings</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accounting Mode
                    </label>
                    <select
                      value={settingsData.accountingMode}
                      onChange={handleAccountingModeChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="gl_only">GL Only</option>
                      <option value="pos_integrated">POS Integrated</option>
                      <option value="erp_subledger">ERP Subledger</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Compliance Profile
                    </label>
                    <select
                      value={settingsData.complianceProfile}
                      onChange={handleComplianceProfileChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="none">None</option>
                      <option value="bir_books">BIR Books</option>
                      <option value="bir_vat">BIR VAT</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Section 3: Enabled Modules */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Feature Flags (Enabled Modules)</h3>
                
                {/* Accounting Core */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-700 mb-3">Accounting Core</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderModuleToggle('accountingCore', 'Accounting Core')}
                    {renderModuleToggle('accountingReports', 'Accounting Reports')}
                  </div>
                </div>

                {/* Compliance/Tax */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-700 mb-3">Compliance & Tax</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderModuleToggle('birBooks', 'BIR Books')}
                    {renderModuleToggle('tax', 'Tax')}
                    {renderModuleToggle('vat', 'VAT')}
                  </div>
                </div>

                {/* Subledgers */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-700 mb-3">Subledgers</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderModuleToggle('ar', 'Accounts Receivable (AR)')}
                    {renderModuleToggle('ap', 'Accounts Payable (AP)')}
                    {renderModuleToggle('invoicing', 'Invoicing')}
                    {renderModuleToggle('billing', 'Billing')}
                  </div>
                </div>

                {/* POS Suite */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-700 mb-3">POS Suite</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {renderModuleToggle('pos', 'POS')}
                    {renderModuleToggle('inventory', 'Inventory')}
                    {renderModuleToggle('attendance', 'Attendance')}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3 border-t border-gray-300 pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettingsModal;
